import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, MapPin, Plane, Building2, Car, Home, Sparkles, FileText, HelpCircle, CreditCard, Briefcase, Users, Settings, Globe } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { User } from '@supabase/supabase-js';
import { useAuth } from "@/contexts/AuthProvider";
import Header from "@/components/layout/Header";
import HomeTab from "@/components/employee/HomeTab";
import CommunityTab from "@/components/employee/CommunityTab";
import AccountTab from "@/components/employee/AccountTab";
import TripsTab from "@/components/employee/TripsTab";
import ExploreTab from "@/components/employee/ExploreTab";

interface Employee {
  id: string;
  company_id: string;
  profile_id: string;
  current_country?: string;
  companies: {
    name: string;
  };
}

interface Profile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  position?: string;
  residence_country?: string;
}

interface Points {
  id: string;
  total_points: number;
  used_points: number;
  month: number;
  year: number;
}

interface FeaturedDestination {
  id: string;
  name: string;
  photo_url?: string;
  points_required: number;
  description?: string;
  category: string;
}

const EmployeeDashboard = () => {
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [currentPoints, setCurrentPoints] = useState<Points | null>(null);
  const [destinations, setDestinations] = useState<FeaturedDestination[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("home");
const { session, initialized } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

useEffect(() => {
    console.debug('[EmployeeDashboard] auth check', { initialized, hasSession: !!session });
    if (!initialized) return;
    if (!session?.user) return;
    checkUserRole(session.user);
  }, [initialized, session]);

  const checkUserRole = async (user: User) => {
    if (!user) {
      navigate('/auth');
      return;
    }

    const { data: profile } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (!profile || profile.role !== 'employee') {
      navigate('/');
      return;
    }

    setProfile(profile);

    const { data: employeeData, error: employeeError } = await supabase
      .from('employees')
      .select(`
        id,
        company_id,
        profile_id,
        current_country,
        companies (
          name
        )
      `)
      .eq('profile_id', profile.id)
      .maybeSingle();

    if (!employeeData || employeeError) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Profil employé non trouvé"
      });
      navigate('/');
      return;
    }

    setEmployee(employeeData);
    await Promise.all([
      fetchCurrentPoints(employeeData.id),
      fetchDestinations()
    ]);
    setLoading(false);
  };

  const fetchCurrentPoints = async (employeeId: string) => {
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();

    try {
      const { data, error } = await supabase
        .from('employee_points')
        .select('*')
        .eq('employee_id', employeeId)
        .eq('month', currentMonth)
        .eq('year', currentYear)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      
      if (!data) {
        const { data: newPoints, error: insertError } = await supabase
          .from('employee_points')
          .insert({
            employee_id: employeeId,
            month: currentMonth,
            year: currentYear,
            total_points: 10,
            used_points: 0
          })
          .select()
          .single();

        if (insertError) throw insertError;
        setCurrentPoints(newPoints);
      } else {
        setCurrentPoints(data);
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de charger vos points"
      });
    }
  };

  const fetchDestinations = async () => {
    try {
      const { data, error } = await supabase
        .from('featured_destinations')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(8);

      if (error) throw error;
      setDestinations(data || []);
    } catch (error: any) {
      console.error('Error fetching destinations:', error);
    }
  };

  const handleRequestSubmitted = async () => {
    if (employee) {
      await fetchCurrentPoints(employee.id);
    }
  };

  const handleProfileUpdate = (updatedProfile: Profile) => {
    setProfile(updatedProfile);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-subtle">
        <Header showLogoutButton={true} />
        <div className="container mx-auto py-16 px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Chargement de votre dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <Header showLogoutButton={true} />
      <div className="container mx-auto py-4 sm:py-8 px-4 pt-20 sm:pt-24">
        <div className="max-w-6xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="sticky top-[4.5rem] z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border flex w-full justify-between mb-6 sm:mb-8 h-auto p-1 px-2 bg-muted/50 overflow-x-auto gap-2">
              <TabsTrigger value="home" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 sm:py-1.5 text-xs sm:text-sm flex-shrink-0 min-w-0">
                <Home className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="text-xs sm:text-sm">Accueil</span>
              </TabsTrigger>
              <TabsTrigger value="community" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 sm:py-1.5 text-xs sm:text-sm flex-shrink-0 min-w-0">
                <Users className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="text-xs sm:text-sm">Communauté</span>
              </TabsTrigger>
              <TabsTrigger value="account" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 sm:py-1.5 text-xs sm:text-sm flex-shrink-0 min-w-0">
                <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="text-xs sm:text-sm hidden sm:inline">Mon compte</span>
                <span className="text-xs sm:hidden">Compte</span>
              </TabsTrigger>
              <TabsTrigger value="trips" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 sm:py-1.5 text-xs sm:text-sm flex-shrink-0 min-w-0">
                <MapPin className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="text-xs sm:text-sm hidden sm:inline">Mes voyages</span>
                <span className="text-xs sm:hidden">Voyages</span>
              </TabsTrigger>
              <TabsTrigger value="explore" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 sm:py-1.5 text-xs sm:text-sm flex-shrink-0 min-w-0">
                <Globe className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="text-xs sm:text-sm">Explorer</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="home" className="space-y-6">
              <HomeTab 
                employee={employee}
                profile={profile}
                currentPoints={currentPoints}
                destinations={destinations}
                onRequestSubmitted={handleRequestSubmitted}
              />
            </TabsContent>

            <TabsContent value="community" className="space-y-6">
              <CommunityTab currentProfile={profile} />
            </TabsContent>

            <TabsContent value="account" className="space-y-6">
              <AccountTab 
                profile={profile} 
                onProfileUpdate={handleProfileUpdate}
              />
            </TabsContent>

            <TabsContent value="trips" className="space-y-6">
              <TripsTab employeeId={employee?.id || null} />
            </TabsContent>

            <TabsContent value="explore" className="space-y-6">
              <ExploreTab employee={employee} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default EmployeeDashboard;